import { FC } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { pxToRem } from 'utils/convert';
import { THEME } from 'ui/deprecated/theme/Theme';

import { DefaultIterableThemeType } from './types';
import {
  backgroundColors,
  brandColors,
  linkColors,
  neutrals,
  primaryColors,
  secondaryColors,
  tertiaryColors,
  textColors,
  utilityColors,
  standardChartColors,
  comparisonChartColors,
  relatedChartColors,
} from './colors';
import {
  buttonShadow,
  extraLargeShadow,
  insetShadow,
  largeShadow,
  mediumShadow,
  smallShadow,
  subtleShadow,
} from './shadows';
import * as spacing from './spacing';
import {
  body1,
  body1Bold,
  body2,
  body2Bold,
  button,
  caption,
  fonts,
  fontSizes,
  fontWeights,
  heading1,
  heading2,
  heading3,
  heading4,
  letterSpacing,
  link,
  link2,
  monospace1,
  monospace2,
  overline,
  subtitle1,
  subtitle2,
  zIndex,
} from './typography/styles';

const { scaleBase, layoutLRPadding, layoutTopPadding, ...spaceValues } = spacing;

export const DefaultIterableTheme: DefaultIterableThemeType = {
  ...THEME,
  colors: {
    ...THEME.colors,
    backgroundColors,
    brandColors,
    linkColors,
    neutrals,
    primaryColors,
    secondaryColors,
    tertiaryColors,
    textColors,
    utilityColors,
    standardChartColors,
    comparisonChartColors,
    relatedChartColors,
  },
  elevations: {
    buttonShadow,
    clickShadow: insetShadow,
    extraLargeShadow,
    largeShadow,
    mediumShadow,
    smallShadow,
    subtleShadow,
  },
  spacing: {
    ...THEME.spacing,
    utils: {
      pxToRem,
      scaleBase,
    },
    values: {
      ...spaceValues,
      layoutHorizontal: layoutLRPadding,
      layoutVertical: layoutTopPadding,
    },
  },
  styles: {
    body: {
      body1,
      body1Strong: body1Bold,
      body2,
      body2Strong: body2Bold,
    },
    buttons: {
      button,
    },
    captions: {
      caption1: caption,
    },
    fonts: {
      text: fonts.sansSerif,
      code: fonts.monospaced,
    },
    fontSizes,
    fontWeights,
    headings: {
      heading1,
      heading2,
      heading3,
      heading4,
    },
    letterSpacing,
    links: {
      link1: link,
      link2,
    },
    monospace: {
      monospace1,
      monospace2,
    },
    overlines: {
      overline1: overline,
    },
    subtitles: {
      subtitle1,
      subtitle2,
    },
    zIndex,
  },
};

export const ThemeProvider: FC<React.PropsWithChildren<unknown>> = (props) => (
  <StyledThemeProvider theme={DefaultIterableTheme}>{props.children}</StyledThemeProvider>
);
